import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Gauge, Download, RefreshCw, AlertCircle, AlertTriangle, CheckCircle,
  ArrowRightLeft, Filter, Info,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NavBar } from './NavBar';
import { ExportButtons } from './ExportButtons';
import { csvCell } from '../utils/csvSafe';
import { getUploadStatusList } from '../services/uploadService';

// Capacity Limits report.
//
// Two report ids on the existing reports endpoint:
//   dashboard.capacity_limits        one month, one row per limit in force
//   dashboard.capacity_limits_trend  the last N months, one row per limit per month
//
// Rules this page follows (capacity_limits_dashboard_handoff.md):
//   - the company comes from the signed-in token, never from this page;
//   - limits are PER MONTH: months are shown side by side and never added together;
//   - a month with no rows is an empty state, not an error;
//   - the server's `detail` sentence is shown as written;
//   - colour is never the only signal: every status has an icon and a text label;
//   - the report is read-only.

// ─── Types ────────────────────────────────────────────────────────────────────

type Status = 'OVER_LIMIT' | 'CAP_APPLIED' | 'NEAR_LIMIT' | 'OK';

// The API returns every value as a string or null.
interface LimitRow {
  limit_type: string;
  service_area_code: string | null;
  service_area_name: string | null;
  from_facility_code: string | null;
  from_facility_name: string | null;
  to_facility_code: string | null;
  to_facility_name: string | null;
  period_year: string | null;
  period_month: string | null;
  period_month_name: string | null;
  max_tons: string | null;
  measured_tons: string | null;
  uncapped_tons: string | null;
  utilization_percent: string | null;
  overage_tons: string | null;
  status: string | null;
  detail?: string | null;      // month view only
}

type View = 'month' | 'trend';

interface Loaded {
  view: View;
  year: number;
  month: number;
  months: number;              // trend window (1 for the month view)
  rows: LimitRow[];
  truncated: boolean;
  updating: boolean;           // the month is queued or processing on the upload-status page
}

const MONTHS = [
  { value: '1',  label: 'January'   }, { value: '2',  label: 'February'  },
  { value: '3',  label: 'March'     }, { value: '4',  label: 'April'     },
  { value: '5',  label: 'May'       }, { value: '6',  label: 'June'      },
  { value: '7',  label: 'July'      }, { value: '8',  label: 'August'    },
  { value: '9',  label: 'September' }, { value: '10', label: 'October'   },
  { value: '11', label: 'November'  }, { value: '12', label: 'December'  },
];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TREND_WINDOWS = [3, 6, 12, 24];

// Open on the previous month: companies upload and process after month-end, so the
// current month is usually still empty (same default as the Dashboard).
const today = new Date();
const CURRENT_YEAR = today.getFullYear();
const DEFAULT_YEAR = today.getMonth() === 0 ? CURRENT_YEAR - 1 : CURRENT_YEAR;
const DEFAULT_MONTH = today.getMonth() === 0 ? 12 : today.getMonth();   // previous month, 1-12

const STATUS_ORDER: Status[] = ['OVER_LIMIT', 'CAP_APPLIED', 'NEAR_LIMIT', 'OK'];

interface StatusStyle {
  label: string;
  short: string;
  meaning: string;
  Icon: React.ComponentType<{ className?: string }>;
  badge: string;      // pill: background, text, border
  bar: string;        // usage bar fill
  tile: string;       // summary tile accent
  cell: string;       // trend cell
}

const STATUS_STYLES: Record<Status, StatusStyle> = {
  OVER_LIMIT: {
    label: 'Over limit', short: 'Over',
    meaning: 'Tons are above the limit.',
    Icon: AlertCircle,
    badge: 'bg-red-50 text-red-700 border-red-200',
    bar: 'bg-red-600',
    tile: 'border-red-200 bg-red-50 text-red-700',
    cell: 'bg-red-100 text-red-800',
  },
  CAP_APPLIED: {
    label: 'Cap applied', short: 'Capped',
    meaning: 'The service area was held to its limit and the excess was reassigned to other service areas.',
    Icon: ArrowRightLeft,
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    bar: 'bg-indigo-600',
    tile: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    cell: 'bg-indigo-100 text-indigo-800',
  },
  NEAR_LIMIT: {
    label: 'Near limit', short: 'Near',
    meaning: '90% or more of the limit is used.',
    Icon: AlertTriangle,
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
    bar: 'bg-amber-500',
    tile: 'border-amber-200 bg-amber-50 text-amber-800',
    cell: 'bg-amber-100 text-amber-900',
  },
  OK: {
    label: 'Within limit', short: 'OK',
    meaning: 'Under 90% of the limit is used.',
    Icon: CheckCircle,
    badge: 'bg-green-50 text-green-700 border-green-200',
    bar: 'bg-green-600',
    tile: 'border-green-200 bg-green-50 text-green-700',
    cell: 'bg-green-50 text-green-800',
  },
};

// A status this page does not know (a future value) is shown as written, with neutral styling.
function styleFor(status: string | null): StatusStyle {
  if (status && Object.prototype.hasOwnProperty.call(STATUS_STYLES, status)) {
    return STATUS_STYLES[status as Status];
  }
  return {
    label: status || 'Unknown', short: status || '?', meaning: '',
    Icon: Info,
    badge: 'bg-gray-50 text-gray-700 border-gray-200',
    bar: 'bg-gray-500',
    tile: 'border-gray-200 bg-gray-50 text-gray-700',
    cell: 'bg-gray-100 text-gray-800',
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function num(s: string | null | undefined): number | null {
  if (s === null || s === undefined || s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function tons(s: string | null | undefined): string {
  const n = num(s);
  return n === null ? '—' : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function pct(s: string | null | undefined): string {
  const n = num(s);
  return n === null ? '—' : `${n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function monthLabel(year: number, month: number): string {
  return `${MONTHS[month - 1]?.label ?? month} ${year}`;
}

/** The party the limit applies to: a service area, or an originating facility. */
function scopeName(r: LimitRow): string {
  if (r.limit_type === 'SERVICE_AREA') return r.service_area_name || r.service_area_code || 'Unnamed service area';
  return r.from_facility_name || r.from_facility_code || 'Unnamed facility';
}

function disposalName(r: LimitRow): string {
  return r.to_facility_name || r.to_facility_code || 'Unnamed facility';
}

/** One series per limit: type | service area or originating facility | disposal facility. */
function seriesKey(r: LimitRow): string {
  const scope = r.limit_type === 'SERVICE_AREA' ? r.service_area_code : r.from_facility_code;
  return [r.limit_type, scope ?? '', r.to_facility_code ?? ''].join('|');
}

function monthIndex(year: number, month: number): number {
  return year * 12 + month;     // same arithmetic the API uses for the trend window
}

/** The months in the trend window, oldest first, ending at year/month. */
function trendWindow(year: number, month: number, months: number): { year: number; month: number }[] {
  const end = monthIndex(year, month) - 1;           // zero-based month count
  const out: { year: number; month: number }[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const idx = end - i;
    out.push({ year: Math.floor(idx / 12), month: (idx % 12) + 1 });
  }
  return out;
}

function validPeriod(year: string, month: string): boolean {
  const y = Number(year), m = Number(month);
  return Number.isInteger(y) && y >= 2000 && y <= 2100 && Number.isInteger(m) && m >= 1 && m <= 12;
}

// The server's error text can contain query details, so it is never shown.
function friendlyError(status: number | null): string {
  if (status === 401 || status === 403) {
    return 'You do not have access to this report. Sign in again, or contact your administrator.';
  }
  if (status === 400) {
    return 'The year or month was not accepted. Check them and try again.';
  }
  return 'The report could not be loaded. Try again.';
}

function downloadCsv(filename: string, headers: string[], rows: string[][]) {
  const csv = [headers.map(csvCell).join(','), ...rows.map(r => r.map(csvCell).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Pieces ───────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string | null }) {
  const s = styleFor(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold whitespace-nowrap ${s.badge}`}>
      <s.Icon className="w-3.5 h-3.5" />
      {s.label}
    </span>
  );
}

/** Usage bar. The scale runs to 100%, or further when the limit is exceeded; the tick marks 100%. */
function UsageBar({ row }: { row: LimitRow }) {
  const util = num(row.utilization_percent) ?? 0;
  const scaleMax = Math.max(100, util);
  const fill = Math.max(0, Math.min(100, (util / scaleMax) * 100));
  const tick = (100 / scaleMax) * 100;
  const s = styleFor(row.status);
  return (
    <div className="min-w-[8rem]">
      <div
        role="img"
        aria-label={`${pct(row.utilization_percent)} of the limit used`}
        className="relative h-2.5 rounded-full bg-gray-100"
      >
        <div className={`h-2.5 rounded-full ${s.bar}`} style={{ width: `${fill}%` }} />
        {util > 100 && (
          <div className="absolute top-[-3px] h-4 w-0.5 bg-gray-700" style={{ left: `${tick}%` }} title="100% of the limit" />
        )}
      </div>
      <p className="mt-1 text-xs font-medium text-gray-700">{pct(row.utilization_percent)}</p>
    </div>
  );
}

function SummaryTiles({ rows }: { rows: LimitRow[] }) {
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach(r => { const k = r.status ?? ''; c[k] = (c[k] ?? 0) + 1; });
    return c;
  }, [rows]);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {STATUS_ORDER.map(st => {
        const s = STATUS_STYLES[st];
        const n = counts[st] ?? 0;
        return (
          <div key={st} className={`rounded-xl border shadow-sm p-4 ${n > 0 ? s.tile : 'border-gray-200 bg-white text-gray-500'}`}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
              <s.Icon className="w-4 h-4" />
              {s.label}
            </div>
            <p className="mt-2 text-2xl font-bold">{n}</p>
            <p className="text-xs opacity-80">{n === 1 ? 'limit' : 'limits'}</p>
          </div>
        );
      })}
    </div>
  );
}

function MonthTable({ title, subtitle, rows }: { title: string; subtitle: string; rows: LimitRow[] }) {
  if (rows.length === 0) return null;
  const isServiceArea = rows[0].limit_type === 'SERVICE_AREA';
  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-bold text-gray-900">{title}</h2>
        <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
      </div>
      <ul className="md:hidden divide-y divide-gray-100">
        {rows.map((r, i) => {
          const over = num(r.overage_tons) ?? 0;
          return (
            <li key={`${seriesKey(r)}|${i}`} className="px-4 py-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{scopeName(r)}</p>
                  <p className="text-xs text-gray-500">to {disposalName(r)}</p>
                </div>
                <StatusBadge status={r.status} />
              </div>
              <UsageBar row={r} />
              <p className="text-xs text-gray-700">
                {tons(r.measured_tons)} of {tons(r.max_tons)} tons counted
                {r.status === 'CAP_APPLIED' && r.uncapped_tons !== null && <span className="text-indigo-700"> (before cap: {tons(r.uncapped_tons)})</span>}
                {over > 0 && <span className="font-semibold text-red-700"> (over by {tons(r.overage_tons)})</span>}
              </p>
              {r.detail && <p className="text-xs text-gray-600">{r.detail}</p>}
            </li>
          );
        })}
      </ul>
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <th scope="col" className="text-left px-5 py-2.5">{isServiceArea ? 'Service area' : 'Originating facility'}</th>
              <th scope="col" className="text-left px-3 py-2.5">Disposal facility</th>
              <th scope="col" className="text-right px-3 py-2.5">Limit (tons)</th>
              <th scope="col" className="text-right px-3 py-2.5">Counted (tons)</th>
              <th scope="col" className="text-left px-3 py-2.5">Usage</th>
              <th scope="col" className="text-left px-5 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r, i) => {
              const over = num(r.overage_tons) ?? 0;
              return (
                <tr key={`${seriesKey(r)}|${i}`} className="align-top">
                  <td className="px-5 py-3 font-medium text-gray-900">{scopeName(r)}</td>
                  <td className="px-3 py-3 text-gray-700">{disposalName(r)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-700">{tons(r.max_tons)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-900">
                    {tons(r.measured_tons)}
                    {r.status === 'CAP_APPLIED' && r.uncapped_tons !== null && (
                      <div className="text-xs text-indigo-700">before cap: {tons(r.uncapped_tons)}</div>
                    )}
                    {over > 0 && (
                      <div className="text-xs font-semibold text-red-700">over by {tons(r.overage_tons)}</div>
                    )}
                  </td>
                  <td className="px-3 py-3"><UsageBar row={r} /></td>
                  <td className="px-5 py-3">
                    <StatusBadge status={r.status} />
                    {r.detail && <p className="mt-1.5 text-xs text-gray-600 max-w-xs">{r.detail}</p>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface Series {
  key: string;
  limitType: string;
  scope: string;
  disposal: string;
  byMonth: Map<number, LimitRow>;    // monthIndex -> row; months with no limit have no entry
}

function buildSeries(rows: LimitRow[]): Series[] {
  const map = new Map<string, Series>();
  const order: string[] = [];
  rows.forEach(r => {
    const y = num(r.period_year), m = num(r.period_month);
    if (y === null || m === null) return;
    const k = seriesKey(r);
    let s = map.get(k);
    if (!s) {
      s = { key: k, limitType: r.limit_type, scope: scopeName(r), disposal: disposalName(r), byMonth: new Map() };
      map.set(k, s);
      order.push(k);
    }
    s.byMonth.set(monthIndex(y, m), r);
  });
  return order.map(k => map.get(k) as Series);
}

function TrendTable({ title, series, cols }: { title: string; series: Series[]; cols: { year: number; month: number }[] }) {
  if (series.length === 0) return null;
  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-bold text-gray-900">{title}</h2>
        <p className="text-xs text-gray-500 mt-0.5">Percent of each month's limit used. A dash means no limit was in force that month.</p>
      </div>
      <div className="relative overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="bg-gray-50 font-semibold text-gray-500 uppercase tracking-wide">
              <th scope="col" className="sticky left-0 z-10 bg-gray-50 text-left px-4 py-2.5 min-w-[12rem]">Limit</th>
              {cols.map(w => (
                <th key={`${w.year}-${w.month}`} scope="col" className="px-2 py-2.5 text-center whitespace-nowrap">
                  {MONTH_SHORT[w.month - 1]}
                  <div className="font-normal normal-case text-gray-400">{w.year}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {series.map(s => (
              <tr key={s.key}>
                <th scope="row" className="sticky left-0 z-10 bg-white text-left px-4 py-2 font-normal">
                  <div className="font-medium text-gray-900">{s.scope}</div>
                  <div className="text-gray-500">to {s.disposal}</div>
                </th>
                {cols.map(w => {
                  const r = s.byMonth.get(monthIndex(w.year, w.month));
                  if (!r) {
                    return <td key={`${w.year}-${w.month}`} className="px-2 py-2 text-center text-gray-300"><span aria-label="No limit in force">—</span></td>;
                  }
                  const st = styleFor(r.status);
                  const hint = `${monthLabel(w.year, w.month)}: ${tons(r.measured_tons)} of ${tons(r.max_tons)} tons (${pct(r.utilization_percent)}). ${st.label}.`;
                  return (
                    <td key={`${w.year}-${w.month}`} className="px-1 py-1.5 text-center">
                      <div className={`rounded-md px-1.5 py-1 ${st.cell}`} title={hint}>
                        <div className="font-semibold tabular-nums">{pct(r.utilization_percent)}</div>
                        <div className="flex items-center justify-center gap-0.5 text-[10px] font-medium">
                          <st.Icon className="w-3 h-3" />
                          <span>{st.short}</span>
                        </div>
                        <span className="sr-only">{hint}</span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CapacityLimits() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  const [view, setView] = useState<View>('month');
  const [year, setYear] = useState<string>(String(DEFAULT_YEAR));
  const [month, setMonth] = useState<string>(String(DEFAULT_MONTH));
  const [months, setMonths] = useState<string>('12');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const requestId = useRef(0);

  const canRun = validPeriod(year, month) && !loading;

  const fetchReport = async () => {
    if (!validPeriod(year, month)) return;
    const mine = ++requestId.current;            // a slower, older request must not overwrite a newer one
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const y = Number(year), m = Number(month);
      const n = view === 'trend' ? Number(months) : 1;
      const params: Record<string, string> = { year: String(y), month: String(m) };
      if (view === 'trend') params.months = String(n);

      const res = await fetch(`${API_URL}/reports/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_id: view === 'trend' ? 'dashboard.capacity_limits_trend' : 'dashboard.capacity_limits',
          params,
        }),
      });
      if (!res.ok) {
        console.error('Capacity limits report failed with HTTP', res.status);
        if (mine === requestId.current) setError(friendlyError(res.status));
        return;
      }
      const result = await res.json();
      const rows: LimitRow[] = Array.isArray(result.data) ? result.data : [];

      // "Being updated" note: the month is queued or processing on the upload-status page.
      // The note is a courtesy, so a failure here never blocks the report.
      let updating = false;
      try {
        const list = await getUploadStatusList();
        updating = (list.rebuilds ?? []).some(rb =>
          Number(rb.year) === y && Number(rb.month) === m &&
          (rb.status === 'queued' || rb.status === 'processing'));
      } catch {
        updating = false;
      }

      if (mine !== requestId.current) return;
      setLoaded({ view, year: y, month: m, months: n, rows, truncated: result.truncated === true, updating });
    } catch (e) {
      console.error('Capacity limits report error:', e);
      if (mine === requestId.current) setError(friendlyError(null));
    } finally {
      if (mine === requestId.current) setLoading(false);
    }
  };

  // Switching between the month and trend views clears the old result so the two never mix.
  const switchView = (v: View) => {
    if (v === view) return;
    requestId.current++;
    setView(v);
    setLoaded(null);
    setError(null);
    setLoading(false);
  };

  const rows = loaded?.rows ?? [];
  const serviceAreaRows = rows.filter(r => r.limit_type === 'SERVICE_AREA');
  const facilityRows = rows.filter(r => r.limit_type === 'FACILITY');
  const otherRows = rows.filter(r => r.limit_type !== 'SERVICE_AREA' && r.limit_type !== 'FACILITY');

  const trendSeries = useMemo(() => (loaded?.view === 'trend' ? buildSeries(loaded.rows) : []), [loaded]);
  const trendCols = useMemo(
    () => (loaded?.view === 'trend' ? trendWindow(loaded.year, loaded.month, loaded.months) : []),
    [loaded],
  );

  // One flat table serves both CSV and the PDF / Excel export buttons.
  const exportTable = (): { columns: string[]; data: Record<string, string>[]; csvRows: string[][] } => {
    if (!loaded) return { columns: [], data: [], csvRows: [] };
    const isMonth = loaded.view === 'month';
    const columns = [
      ...(isMonth ? [] : ['year', 'month']),
      'limit_type', 'applies_to', 'disposal_facility', 'limit_tons', 'counted_tons', 'before_cap_tons',
      'utilization_percent', 'overage_tons', 'status', ...(isMonth ? ['detail'] : []),
    ];
    const data = loaded.rows.map(r => {
      const rec: Record<string, string> = {
        year: r.period_year ?? '',
        month: r.period_month_name ?? r.period_month ?? '',
        limit_type: r.limit_type === 'SERVICE_AREA' ? 'Service area' : r.limit_type === 'FACILITY' ? 'Facility' : r.limit_type,
        applies_to: scopeName(r),
        disposal_facility: disposalName(r),
        limit_tons: r.max_tons ?? '',
        counted_tons: r.measured_tons ?? '',
        before_cap_tons: r.uncapped_tons ?? '',
        utilization_percent: r.utilization_percent ?? '',
        overage_tons: r.overage_tons ?? '',
        status: styleFor(r.status).label,
        detail: r.detail ?? '',
      };
      return rec;
    });
    return { columns, data, csvRows: data.map(d => columns.map(c => d[c])) };
  };

  const exportCSV = () => {
    if (!loaded || loaded.rows.length === 0) return;
    const t = exportTable();
    const mm = String(loaded.month).padStart(2, '0');
    const name = loaded.view === 'month'
      ? `capacity-limits-${loaded.year}-${mm}.csv`
      : `capacity-limits-trend-${loaded.year}-${mm}-${loaded.months}mo.csv`;
    downloadCsv(name, t.columns, t.csvRows);
  };

  const periodText = loaded
    ? loaded.view === 'month'
      ? monthLabel(loaded.year, loaded.month)
      : `${loaded.months} months ending ${monthLabel(loaded.year, loaded.month)}`
    : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-teal-600 rounded-lg flex-shrink-0">
              <Gauge className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Landfill Capacity Limits</h1>
              <p className="text-sm text-gray-500">Monthly tonnage limits, tons counted against them, and what needs attention</p>
            </div>
          </div>
          {loaded && loaded.rows.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={exportCSV}
                className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors">
                <Download className="w-4 h-4" /><span>Export CSV</span>
              </button>
              <ExportButtons
                reportTitle={loaded.view === 'month' ? 'Landfill Capacity Limits' : 'Landfill Capacity Limits Trend'}
                subtitle={periodText}
                getExportData={() => {
                  const t = exportTable();
                  return { columns: t.columns, data: t.data };
                }}
              />
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Filters</span>
            </div>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm" role="group" aria-label="Report view">
              {([['month', 'One month'], ['trend', 'Trend']] as [View, string][]).map(([v, label]) => (
                <button key={v} type="button" onClick={() => switchView(v)} aria-pressed={view === v}
                  className={`px-4 py-1.5 font-medium transition-colors ${view === v ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="cl-year" className="block text-xs font-medium text-gray-600 mb-1">
                {view === 'trend' ? 'Ending year' : 'Year'} <span className="text-red-500">*</span>
              </label>
              <input id="cl-year" type="number" value={year} min="2020" max={CURRENT_YEAR}
                onChange={e => setYear(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label htmlFor="cl-month" className="block text-xs font-medium text-gray-600 mb-1">
                {view === 'trend' ? 'Ending month' : 'Month'} <span className="text-red-500">*</span>
              </label>
              <select id="cl-month" value={month} onChange={e => setMonth(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            {view === 'trend' && (
              <div>
                <label htmlFor="cl-months" className="block text-xs font-medium text-gray-600 mb-1">Months shown</label>
                <select id="cl-months" value={months} onChange={e => setMonths(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
                  {TREND_WINDOWS.map(n => <option key={n} value={String(n)}>{n} months</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={fetchReport} disabled={!canRun}
              className="flex items-center space-x-2 px-6 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading…' : 'Run Report'}</span>
            </button>
          </div>
        </div>

        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">Failed to load report</p>
              <p className="text-sm text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {loaded && loaded.updating && (
          <div role="status" className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3">
            <RefreshCw className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-800">{monthLabel(loaded.year, loaded.month)} is being updated</p>
              <p className="text-sm text-blue-700 mt-0.5">
                Newly uploaded data for this month is still being processed. The limits and figures below may change when it finishes.
              </p>
            </div>
          </div>
        )}

        {loaded && loaded.truncated && (
          <div role="status" className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              There are too many limits to show every month. Some rows are missing; choose fewer months and run the report again.
            </p>
          </div>
        )}

        {loaded && loaded.rows.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-16 px-4">
            <Gauge className="w-12 h-12 mx-auto mb-3 text-gray-200" />
            <p className="text-sm font-medium text-gray-600">
              {loaded.view === 'month'
                ? 'No capacity limits are configured for this month.'
                : 'No capacity limits were found for these months.'}
            </p>
            <p className="text-sm text-gray-400 mt-1 max-w-xl mx-auto">
              Limits are set by uploading a Landfill Capacity Limits file. A limit uploaded or changed recently appears here once the month has been processed.
            </p>
            <button type="button" onClick={() => navigate('/upload')}
              className="mt-4 text-sm font-medium text-teal-700 hover:text-teal-800 underline">
              Go to Upload Files
            </button>
          </div>
        )}

        {loaded && loaded.rows.length > 0 && loaded.view === 'month' && (
          <>
            <SummaryTiles rows={rows} />
            <MonthTable
              title="Service area limits"
              subtitle="A disposal facility accepts at most this many tons a month from one service area. A service area over its limit is capped and the excess is reassigned."
              rows={serviceAreaRows}
            />
            <MonthTable
              title="Facility limits"
              subtitle="An originating facility may ship at most this many tons a month to a disposal facility. Shipped tons are actuals; they are flagged, never changed."
              rows={facilityRows}
            />
            <MonthTable title="Other limits" subtitle="Limit types this page does not describe." rows={otherRows} />
            <p className="text-xs text-gray-500">
              Every limit is a monthly limit. Percentages are for {monthLabel(loaded.year, loaded.month)} only; use the Trend view to compare months.
            </p>
          </>
        )}

        {loaded && loaded.rows.length > 0 && loaded.view === 'trend' && (
          <>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-600">
              {STATUS_ORDER.map(st => {
                const s = STATUS_STYLES[st];
                return (
                  <span key={st} className="inline-flex items-center gap-1">
                    <s.Icon className="w-3.5 h-3.5" /> <strong>{s.short}</strong> = {s.label.toLowerCase()}
                  </span>
                );
              })}
            </div>
            <TrendTable title="Service area limits" series={trendSeries.filter(s => s.limitType === 'SERVICE_AREA')} cols={trendCols} />
            <TrendTable title="Facility limits" series={trendSeries.filter(s => s.limitType === 'FACILITY')} cols={trendCols} />
            <TrendTable title="Other limits" series={trendSeries.filter(s => s.limitType !== 'SERVICE_AREA' && s.limitType !== 'FACILITY')} cols={trendCols} />
            <p className="text-xs text-gray-500">
              Each month is its own limit period; tons are never added across months. Hover over a cell for the tons behind the percentage.
            </p>
          </>
        )}

        {!loaded && !loading && !error && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-20">
            <Gauge className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <p className="text-base font-medium text-gray-500">Select a period and click Run Report</p>
            <p className="text-sm text-gray-400 mt-1">Year and Month are required</p>
          </div>
        )}
      </main>
    </div>
  );
}
